import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export type TitleStatus = "open" | "partially_paid" | "paid" | "canceled" | "renegotiated";
export const TITLE_STATUS: StatusMap = {
  open: { label: "em aberto", tone: "info" },
  partially_paid: { label: "parcial", tone: "warn" },
  paid: { label: "pago", tone: "ok" },
  canceled: { label: "cancelado", tone: "neutral" },
  renegotiated: { label: "renegociado", tone: "neutral" },
};

export const METHODS = { pix: "PIX", boleto: "Boleto", card: "Cartão", transfer: "Transferência", cash: "Dinheiro", other: "Outro" } as const;
export type Method = keyof typeof METHODS;

export const DRE_GROUPS = [
  { key: "receita_bruta", label: "Receita bruta" },
  { key: "deducoes", label: "(−) Deduções" },
  { key: "custos_diretos", label: "(−) Custos diretos" },
  { key: "despesas_operacionais", label: "(−) Despesas operacionais" },
  { key: "receitas_financeiras", label: "(+) Receitas financeiras" },
  { key: "despesas_financeiras", label: "(−) Despesas financeiras" },
  { key: "distribuicao", label: "(−) Distribuição" },
] as const;

export interface InstallmentView {
  installment_id: string;
  org_id: string;
  receivable_id: string;
  issuer_id: string | null;
  person_id: string | null;
  counterparty_name: string;
  counterparty_doc: string | null;
  description: string;
  origin_type: string | null;
  origin_id: string | null;
  category_id: string | null;
  renegotiation: boolean;
  receivable_status: TitleStatus;
  number: number;
  label: string | null;
  due_date: string;
  amount_cents: number;
  paid_cents: number;
  open_cents: number;
  status: TitleStatus;
  paid_on: string | null;
  fine_pct: number;
  interest_month_pct: number;
  grace_days: number;
  renegotiated_into: string | null;
  days_overdue: number | null;
}

export interface Receivable {
  id: string;
  org_id: string;
  issuer_id: string | null;
  person_id: string | null;
  counterparty_name: string;
  counterparty_doc: string | null;
  origin_type: string | null;
  origin_id: string | null;
  category_id: string | null;
  description: string;
  total_cents: number;
  competence_month: string;
  status: TitleStatus;
  renegotiation: boolean;
  canceled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
}

export interface Receipt {
  id: string;
  receivable_id: string;
  installment_id: string;
  amount_cents: number;
  fine_cents: number;
  interest_cents: number;
  discount_cents: number;
  total_cents: number;
  policy_fine_cents: number;
  policy_interest_cents: number;
  override_reason: string | null;
  paid_on: string;
  account_id: string | null;
  method: Method;
  source: "manual" | "provider" | "bank_match";
  external_ref: string | null;
  created_at: string;
}

export interface Reversal {
  id: string;
  receipt_id?: string;
  payment_id?: string;
  amount_cents: number;
  total_cents: number;
  reason: string;
  reversed_on: string;
  created_at: string;
}

export interface Account {
  id: string;
  org_id: string;
  issuer_id: string | null;
  kind: "bank" | "cash" | "gateway";
  name: string;
  bank_code: string | null;
  branch: string | null;
  account_number: string | null;
  pix_key: string | null;
  active: boolean;
}

export interface Category {
  id: string;
  code: string;
  name: string;
  kind: "revenue" | "expense";
  dre_group: string;
  offer_slugs: string[];
  active: boolean;
}

export interface FinanceSettings {
  fine_pct: number;
  interest_month_pct: number;
  grace_days: number;
  default_due_days: number;
  due_notice_days: number;
  match_min_score: number;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "finance.create_receivable": {
      args: {
        p_org: U; p_counterparty_name: string; p_total_cents: number; p_description: string; p_issuer?: U | null; p_person?: U | null;
        p_counterparty_doc?: string | null; p_origin_type?: string | null; p_origin_id?: U | null; p_installments?: number;
        p_first_due?: string | null; p_interval_months?: number; p_plan?: Array<{ due_date: string; amount_cents: number; label?: string }> | null;
        p_category?: U | null; p_offer_slug?: string | null; p_competence_month?: string | null; p_idempotency_key?: string | null;
      };
      returns: U;
    };
    "finance.settle_installment": {
      args: {
        p_installment: U; p_amount_cents?: number | null; p_paid_on?: string | null; p_method?: Method; p_account?: U | null;
        p_idempotency_key?: string | null; p_external_ref?: string | null; p_fine_cents?: number | null; p_interest_cents?: number | null;
        p_discount_cents?: number; p_override_reason?: string | null;
      };
      returns: { receipt_id: U; total_cents: number; installment_status: TitleStatus; receivable_status: TitleStatus; open_cents: number; idempotent: boolean };
    };
    "finance.reverse_receipt": { args: { p_receipt: U; p_reason: string; p_reversed_on?: string | null }; returns: unknown };
    "finance.cancel_receivable": { args: { p_receivable: U; p_reason: string }; returns: unknown };
    "finance.create_payable": {
      args: {
        p_org: U; p_supplier_name: string; p_total_cents: number; p_description: string; p_issuer?: U | null; p_category?: U | null;
        p_supplier_doc?: string | null; p_installments?: number; p_first_due?: string | null; p_interval_months?: number;
        p_competence_month?: string | null; p_idempotency_key?: string | null;
      };
      returns: U;
    };
    "finance.pay_installment": {
      args: {
        p_installment: U; p_amount_cents?: number | null; p_paid_on?: string | null; p_method?: Method; p_account?: U | null;
        p_idempotency_key?: string | null; p_fine_cents?: number; p_interest_cents?: number; p_discount_cents?: number;
      };
      returns: unknown;
    };
    "finance.reverse_payment": { args: { p_payment: U; p_reason: string; p_reversed_on?: string | null }; returns: unknown };
    "finance.cancel_payable": { args: { p_payable: U; p_reason: string }; returns: unknown };
    "finance.import_statement": {
      args: { p_account: U; p_rows: unknown[]; p_source?: "ofx" | "csv"; p_filename?: string | null; p_file_sha256?: string | null };
      returns: { import_id: U; rows: number; inserted: number; duplicated: number };
    };
    "finance.suggest_matches": {
      args: { p_account: U; p_bank_transaction?: U | null; p_min_score?: number | null; p_limit?: number };
      returns: Array<{
        bank_transaction_id: U; kind: "receivable" | "payable"; installment_id: U; title_id: U; counterparty_name: string; due_date: string;
        open_cents: number; expected_cents: number; score: number; reasons: string[];
      }>;
    };
    "finance.confirm_match": { args: { p_bank_transaction: U; p_installment: U; p_amount_cents?: number | null; p_excess?: "keep" | "fees"; p_score?: number | null }; returns: unknown };
    "finance.ignore_bank_transaction": { args: { p_bank_transaction: U; p_reason: string }; returns: null };
    "finance.late_fee": {
      args: { p_amount_cents: number; p_due: string; p_paid_on: string; p_fine_pct: number; p_interest_month_pct: number; p_grace_days?: number };
      returns: Array<{ days_late: number; fine_cents: number; interest_cents: number }>;
    };
    "finance.split_installments": {
      args: { p_total_cents: number; p_count: number; p_first_due: string; p_interval_months?: number };
      returns: Array<{ number: number; due_date: string; amount_cents: number }>;
    };
    "finance.install_defaults": { args: { p_org: U }; returns: number };
    "finance.upsert_account": {
      args: {
        p_org: U; p_name: string; p_kind?: string; p_issuer?: U | null; p_bank_code?: string | null; p_branch?: string | null;
        p_account_number?: string | null; p_pix_key?: string | null; p_active?: boolean; p_id?: U | null;
      };
      returns: U;
    };
    "finance.upsert_category": { args: { p_org: U; p_code: string; p_name: string; p_kind: string; p_dre_group: string; p_offer_slugs?: string[]; p_active?: boolean }; returns: U };
    "finance.set_settings": { args: { p_org: U; p_patch: Partial<FinanceSettings> }; returns: FinanceSettings };
    "finance.today": { args: Record<string, never>; returns: string };
  }
}

export function useAccounts(orgId: string) {
  return useQuery({
    queryKey: ["finance", "accounts", orgId],
    queryFn: () => unwrap<Account[]>(from("finance", "accounts").select("*").eq("org_id", orgId).order("name")),
  });
}

export function useCategories(orgId: string) {
  return useQuery({
    queryKey: ["finance", "categories", orgId],
    queryFn: () => unwrap<Category[]>(from("finance", "categories").select("*").eq("org_id", orgId).order("code")),
  });
}

export function useFinanceSettings(orgId: string) {
  return useQuery({
    queryKey: ["finance", "settings", orgId],
    queryFn: async () => (await unwrap<FinanceSettings[]>(from("finance", "settings").select("*").eq("org_id", orgId)))[0] ?? null,
  });
}

/** Chave de idempotência por abertura de formulário (reenvio não duplica recebimento). */
export function newIdempotencyKey(prefix: string): string {
  const rnd = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
  return `${prefix}:${rnd}`;
}

export const ORIGIN_LABEL: Record<string, string> = {
  proposal: "Proposta",
  subscription: "Assinatura",
  agreement: "Acordo",
  project: "Projeto",
  payroll_run: "Folha",
  commission_statement: "Comissões",
};

/** Link para a origem polimórfica quando o painel tem a tela. */
export function originHref(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  if (type === "proposal") return `/comercial/propostas/${id}`;
  return null;
}
